/*
 * ONE NUMBER FORMAT FOR THE STRATEGY MICROSITE. Byte-identical in Cortex and
 * Quantum.
 *
 * Owner rule (DJ, 2026-09-23): the microsite shows ACTUAL figures with
 * thousands separators — "311,000", "563,616", "$20,000" — never an
 * abbreviation ("311K", "311.1K", "$1.2M"). Rates keep their own precision
 * (frequency 1.05, CPM $27.00) and are formatted where they are defined.
 *
 * Campaign 53 showed one CTV estimate three ways on one screen: the Impression
 * Outlook total and tile read "311K", the Funnel Architecture CTV row read
 * "311.1K". It was never two forecasts. One canonical figure reached the page
 * through two fields: the tiles formatted the backend's published display value
 * (311,000 — a directional benchmark volume held to three significant figures,
 * because its last digits move with the rounding of the CPM's second decimal),
 * while the funnel row formatted the unrounded 311,102.
 *
 * So the VALUE is chosen before the format. `resolveCanonicalVolume` returns
 * the published display value when the backend supplies one and the exact
 * value otherwise; every surface formats that one number with
 * `formatMetricCount`. The unrounded figure stays in the data, untouched.
 */

const SUFFIX_MULTIPLIERS = Object.freeze({ k: 1e3, m: 1e6, b: 1e9 });

/*
 * Numbers, digit strings ("311,102") and abbreviated labels ("311.1K", "1.2M")
 * all parse to the value they state, so an abbreviation that reaches a surface
 * from upstream is re-rendered as the actual figure instead of passing through.
 */
const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const raw = String(value).replace(/[,\s]/g, "");
  const suffixed = /^(-?\d+(?:\.\d+)?)([kmb])$/i.exec(raw);
  const numeric = suffixed
    ? Number(suffixed[1]) * SUFFIX_MULTIPLIERS[suffixed[2].toLowerCase()]
    : Number(raw);
  return Number.isFinite(numeric) ? numeric : null;
};

/** The canonical display volume of a delivery / estimate row (or a bare number). */
export const resolveCanonicalVolume = (row) => {
  if (row === null || row === undefined) return null;
  if (typeof row !== "object") return toFiniteNumber(row);
  const candidates = [row.displayValue, row.value, row.rawValue, row.estimatedImpressions, row.impressions];
  for (const candidate of candidates) {
    const numeric = toFiniteNumber(candidate);
    if (numeric !== null) return numeric;
  }
  return null;
};

/** An actual count with thousands separators: 311000 → "311,000". */
export const formatMetricCount = (value, fallback = "") => {
  const numeric = toFiniteNumber(value);
  return numeric === null ? fallback : Math.round(numeric).toLocaleString("en-US");
};

/*
 * A range label ("281K–422K", "18.8M – 28.5M") renders as actual figures too,
 * so an upstream range can never smuggle an abbreviation onto the page.
 * Anything that is neither a number nor a numeric range returns `fallback`.
 */
export const formatMetricCountOrRange = (value, fallback = "") => {
  const single = formatMetricCount(value, "");
  if (single) return single;
  const range = /^\s*([^–—-]+?)\s*[–—-]\s*([^–—-]+?)\s*$/.exec(String(value ?? ""));
  if (range) {
    const low = formatMetricCount(range[1], "");
    const high = formatMetricCount(range[2], "");
    if (low && high) return `${low}–${high}`;
  }
  return fallback;
};

/** Screen-reader phrasing for the same figure, in the same digits. */
export const describeMetricCount = (value, singular, plural = `${singular}s`) => {
  const numeric = toFiniteNumber(value);
  if (numeric === null) return "";
  return `${formatMetricCount(numeric)} ${Math.round(numeric) === 1 ? singular : plural}`;
};
